import React from 'react'
import { SkeletonText, Tag } from '@carbon/react'
import type { EmailResponse } from '@sphere/communication/domain'
import { EmailStatus, type EmailRecentSendRow } from '@sphere/communication/domain'
import { formatDateTimeFrShort } from '@sphere/shared/util'
import styles from './EmailRecentSendsList.module.scss'

const STATUS_LABELS: Record<EmailStatus, string> = {
  [EmailStatus.PENDING]: 'En attente',
  [EmailStatus.QUEUED]: 'En file',
  [EmailStatus.SENDING]: 'Envoi…',
  [EmailStatus.SENT]: 'Envoyé',
  [EmailStatus.FAILED]: 'Échec',
  [EmailStatus.CANCELED]: 'Annulé',
}

function recipientPreview(email: EmailResponse): string {
  const recipients = email.recipients ?? []
  const n = email.totalRecipients ?? recipients.length
  const first = recipients[0]
  if (n <= 0) {
    return '—'
  }
  if (n === 1) {
    return first?.email ?? '—'
  }
  const primary = first?.email ?? `${n} destinataires`
  return `${primary} (+${n - 1})`
}

export function mapEmailToRecentSendRow(email: EmailResponse): EmailRecentSendRow {
  const created =
    email.createdAt instanceof Date
      ? email.createdAt.toISOString()
      : String(email.createdAt)

  return {
    id: email.id,
    subject: email.subject,
    createdAt: created,
    status: email.status,
    recipientPreview: recipientPreview(email),
    totalRecipients: email.totalRecipients ?? 0,
  }
}

export interface EmailRecentSendsListProps {
  rows: EmailRecentSendRow[]
  isLoading: boolean
  isError: boolean
}

export const EmailRecentSendsList: React.FC<EmailRecentSendsListProps> = ({
  rows,
  isLoading,
  isError,
}) => {
  if (isLoading) {
    return (
      <div className={styles.skeletonBlock} aria-busy="true">
        <SkeletonText width="40%" heading />
        <SkeletonText width="100%" />
        <SkeletonText width="100%" />
      </div>
    )
  }

  if (isError) {
    return (
      <p className={styles.error} role="alert">
        Impossible de charger l&apos;historique des envois.
      </p>
    )
  }

  if (rows.length === 0) {
    return (
      <div className={styles.placeholder}>Aucun envoi récent pour le moment.</div>
    )
  }

  return (
    <ul className={styles.list}>
      {rows.map(row => (
        <li key={row.id} className={styles.item}>
          <div className={styles.itemHeader}>
            <p className={styles.subject} title={row.subject}>
              {row.subject}
            </p>
            <Tag type="gray" size="sm">
              {STATUS_LABELS[row.status] ?? row.status}
            </Tag>
          </div>
          <p className={styles.meta}>
            {formatDateTimeFrShort(row.createdAt)}
          </p>
          <p className={styles.recipient} title={row.recipientPreview}>
            {row.recipientPreview}
          </p>
        </li>
      ))}
    </ul>
  )
}
